import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { User, LogOut, X, CreditCard, AlertTriangle, CheckCircle, Building2 } from 'lucide-react';
import { CompanyProfileForm } from './CompanyProfileForm';
import { SecurityTab } from './mfa/SecurityTab';
import { EnterpriseUpgradeModal } from './EnterpriseUpgradeModal';
import { usePaymentStatus } from '../hooks/usePaymentStatus';
import { supabase } from '../lib/supabase';
import { ENTERPRISE_PRICING, annualDiscountPercent } from '../lib/enterprisePricing';

export type AccountSettingsTab = 'profile' | 'company' | 'security' | 'subscription';

interface AccountSettingsProps {
  onClose: () => void;
  /** Tab to open on. Defaults to Account Info. */
  initialTab?: AccountSettingsTab;
}

async function redirectToPortal(setLoading: (v: boolean) => void, setError: (v: string | null) => void) {
  setLoading(true);
  setError(null);
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');
    const res = await fetch('/.netlify/functions/create-portal-session', {
      method: 'POST',
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    const data = await res.json();
    if (!res.ok || !data.url) throw new Error(data.error || 'Failed to open billing portal');
    window.location.href = data.url;
  } catch (err) {
    setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    setLoading(false);
  }
}

async function redirectToSubscriptionCheckout(setLoading: (v: boolean) => void, setError: (v: string | null) => void) {
  setLoading(true);
  setError(null);
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');
    const res = await fetch('/.netlify/functions/create-checkout-session', {
      method: 'POST',
      headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'subscription' }),
    });
    const data = await res.json();
    if (!res.ok || !data.url) throw new Error(data.error || 'Failed to start checkout');
    window.location.href = data.url;
  } catch (err) {
    setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    setLoading(false);
  }
}

interface PaymentRecord {
  id: string;
  type: 'invoice' | 'charge';
  date: number;
  description: string;
  amount: number;
  currency: string;
  status: string;
  pdf_url: string | null;
  receipt_url: string | null;
}

function SubscriptionTab() {
  const paymentStatus = usePaymentStatus();
  const [portalLoading, setPortalLoading] = useState(false);
  const [subLoading, setSubLoading] = useState(false);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const [paymentsError, setPaymentsError] = useState<string | null>(null);
  const [userFirmId, setUserFirmId] = useState<string | null | undefined>(undefined);
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);

  useEffect(() => {
    async function fetchFirmStatus() {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { setUserFirmId(null); return; }
      const { data, error } = await supabase
        .from('user_profiles')
        .select('firm_id')
        .eq('id', session.user.id)
        .single();
      if (error) { console.error('Failed to fetch firm status:', error); setUserFirmId(null); return; }
      setUserFirmId(data?.firm_id ?? null);
    }
    fetchFirmStatus();
  }, []);

  useEffect(() => {
    async function fetchPayments() {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) { setPaymentsLoading(false); return; }
        const res = await fetch('/.netlify/functions/get-payment-history', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to load payment history');
        setPayments(data.payments);
      } catch (err) {
        setPaymentsError(err instanceof Error ? err.message : 'Failed to load payment history');
      } finally {
        setPaymentsLoading(false);
      }
    }
    fetchPayments();
  }, []);

  const { subscription_status, subscription_ends_at, loading } = paymentStatus;

  const formattedEndsAt = subscription_ends_at
    ? new Date(subscription_ends_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-blue-100 rounded-lg">
            <CreditCard className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">Subscription</h3>
            <p className="text-sm text-gray-600">Manage your plan, payment method, and payment history</p>
          </div>
        </div>

        {userFirmId !== null && userFirmId !== undefined ? (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
            <Building2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-blue-900 text-sm">Enterprise Firm Member</p>
              <p className="text-xs text-blue-700 mt-1">Your TrueLoad access is provided through your firm's Enterprise plan. Contact your firm admin to manage billing or seat assignments.</p>
            </div>
          </div>
        ) : (
          <>
            {billingError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{billingError}</p>
              </div>
            )}

            {subscription_status === 'active' && (
              <div className="space-y-4">
                <div data-testid="subscription-status-badge" className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-green-900 text-sm">TrueLoad Unlimited ($59/month)</p>
                    <p className="text-xs text-green-700 mt-0.5">All projects included. Cancel anytime.</p>
                  </div>
                </div>
                {subscription_ends_at && (
                  <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-800">
                      Your subscription cancels on {formattedEndsAt}. You'll retain access until then,
                      after which new projects will require a $30 per-project unlock.
                    </p>
                  </div>
                )}
                <button
                  data-testid="btn-manage-subscription"
                  onClick={() => redirectToPortal(setPortalLoading, setBillingError)}
                  disabled={portalLoading}
                  className="w-full py-2.5 px-4 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 transition-colors disabled:bg-blue-400 flex items-center justify-center gap-2"
                >
                  {portalLoading ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />Opening portal...</>
                  ) : subscription_ends_at ? (
                    'Reactivate Subscription'
                  ) : (
                    'Manage Subscription'
                  )}
                </button>
              </div>
            )}

            {subscription_status === 'past_due' && (
              <div className="space-y-4">
                <div className="flex items-start gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-amber-900 text-sm">TrueLoad Unlimited — Payment Failed</p>
                    <p className="text-xs text-amber-700 mt-1">
                      Your last payment did not go through. Please update your payment method to continue accessing unlimited projects.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => redirectToPortal(setPortalLoading, setBillingError)}
                  disabled={portalLoading}
                  className="w-full py-2.5 px-4 bg-amber-600 text-white rounded-lg font-medium text-sm hover:bg-amber-700 transition-colors disabled:bg-amber-400 flex items-center justify-center gap-2"
                >
                  {portalLoading ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />Opening portal...</>
                  ) : (
                    'Update Payment Method'
                  )}
                </button>
              </div>
            )}

            {(subscription_status === 'canceled' || subscription_status === null) && (
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="font-semibold text-gray-900 text-sm">Pay-per-use ($30/project)</p>
                  <p className="text-xs text-gray-600 mt-1">Each project unlocked individually. No monthly fee.</p>
                </div>
                <button
                  data-testid="btn-subscribe-unlimited"
                  onClick={() => redirectToSubscriptionCheckout(setSubLoading, setBillingError)}
                  disabled={subLoading}
                  className="w-full py-2.5 px-4 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 transition-colors disabled:bg-blue-400 flex items-center justify-center gap-2"
                >
                  {subLoading ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />Redirecting...</>
                  ) : (
                    'Switch to Unlimited — $59/month'
                  )}
                </button>
                <p className="text-xs text-gray-400 text-center">
                  Saves money after 2 projects/month. Cancel anytime.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {userFirmId === null && (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg shadow-sm border border-blue-200 p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Need multiple seats? Upgrade to Enterprise.</h3>
              <p className="text-sm text-gray-700 mb-3">
                One firm account for your whole team. Includes a centralized admin dashboard, branded reports with your company logo, shared customer database, and unlimited project access for every seat.
              </p>
              <p className="text-xs text-gray-600">
                From ${ENTERPRISE_PRICING.monthlyBase}/mo base + ${ENTERPRISE_PRICING.monthlySeat}/seat,
                starting at one seat. Save {annualDiscountPercent()}% on annual billing.
              </p>
            </div>
            <button
              onClick={() => setShowEnterpriseModal(true)}
              className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm transition-colors flex-shrink-0"
            >
              Upgrade to Enterprise
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment History</h3>

        {paymentsLoading && (
          <div className="flex items-center justify-center py-6">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent" />
          </div>
        )}

        {!paymentsLoading && paymentsError && (
          <p className="text-sm text-red-600">{paymentsError}</p>
        )}

        {!paymentsLoading && !paymentsError && payments.length === 0 && (
          <p className="text-sm text-gray-500">No payments yet.</p>
        )}

        {!paymentsLoading && !paymentsError && payments.length > 0 && (
          <div className="divide-y divide-gray-100">
            {payments.map((p) => {
              const dateStr = new Date(p.date * 1000).toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', year: 'numeric',
              });
              const amountStr = (p.amount / 100).toLocaleString('en-US', {
                style: 'currency', currency: p.currency.toUpperCase(),
              });
              const isPaid = p.status === 'paid';
              const statusLabel = p.status.charAt(0).toUpperCase() + p.status.slice(1);
              const linkUrl = p.pdf_url || p.receipt_url;
              const linkLabel = p.pdf_url ? 'PDF' : 'Receipt';
              return (
                <div key={p.id} className="flex items-center justify-between py-3">
                  <div className="min-w-0 flex-1 pr-4">
                    <p className="text-sm font-medium text-gray-900">{dateStr}</p>
                    <p className="text-xs text-gray-500 truncate">{p.description}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-sm font-semibold text-gray-900">{amountStr}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      isPaid ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {statusLabel}
                    </span>
                    {linkUrl && (
                      <a
                        href={linkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline"
                      >
                        {linkLabel}
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <p className="text-sm text-gray-600">
          Need help?{' '}
          <a href="mailto:support@trueloadapp.com" className="text-blue-600 hover:underline">
            support@trueloadapp.com
          </a>
        </p>
      </div>

      {showEnterpriseModal && (
        <EnterpriseUpgradeModal
          onClose={() => setShowEnterpriseModal(false)}
          hasActiveIndividualSub={subscription_status === 'active'}
        />
      )}
    </div>
  );
}

export function AccountSettings({ onClose, initialTab = 'profile' }: AccountSettingsProps) {
  const { user, userProfile, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState<AccountSettingsTab>(initialTab);

  const handleSignOut = async () => {
    await signOut();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Account Settings</h2>
          <button
            onClick={onClose}
            data-testid="btn-profile-close"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        <div className="flex border-b border-gray-200 overflow-x-auto">
          <button
            onClick={() => setActiveTab('profile')}
            className={`flex-1 px-4 py-4 font-medium transition-colors whitespace-nowrap text-sm ${
              activeTab === 'profile'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Account Info
          </button>
          <button
            onClick={() => setActiveTab('company')}
            data-testid="tab-company-profile"
            className={`flex-1 px-4 py-4 font-medium transition-colors whitespace-nowrap text-sm ${
              activeTab === 'company'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Company Profile
          </button>
          <button
            data-testid="tab-security"
            onClick={() => setActiveTab('security')}
            className={`flex-1 px-4 py-4 font-medium transition-colors whitespace-nowrap text-sm ${
              activeTab === 'security'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Security
          </button>
          <button
            data-testid="tab-subscription"
            onClick={() => setActiveTab('subscription')}
            className={`flex-1 px-4 py-4 font-medium transition-colors whitespace-nowrap text-sm ${
              activeTab === 'subscription'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Subscription
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <User className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Profile Information</h3>
                    <p className="text-sm text-gray-600">Your account details</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name
                    </label>
                    <div className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                      {userProfile?.full_name || 'Not set'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address
                    </label>
                    <div className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                      {user?.email}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account Created
                    </label>
                    <div className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                      {userProfile?.created_at
                        ? new Date(userProfile.created_at).toLocaleDateString()
                        : 'Unknown'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-red-200 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Sign Out</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Sign out of your account. Your projects will be saved and available when you sign back in.
                </p>
                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  Sign Out
                </button>
              </div>
            </div>
          )}

          {activeTab === 'company' && <CompanyProfileForm />}
          {activeTab === 'security' && <SecurityTab />}
          {activeTab === 'subscription' && <SubscriptionTab />}
        </div>
      </div>
    </div>
  );
}
